import useAxios from '@verifier-user/verifier-axios-hook'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'
import useRaygun from '@verifier-user/verifier-raygun'

export const ApplicationContext = React.createContext()
export const useApplicationContext = () => useContext(ApplicationContext)

let refreshTokenTask

export const ApplicationContextProvider = ({ vendorSettings, children }) => {
  const { get, post } = useAxios()
  const [customerLogin, setCustomerLogin] = useState()
  const [accessCode, setAccessCode] = useState()
  const [accessCodeVerified, setAccessCodeVerified] = useState(false)
  const [accessCodeError, setAccessCodeError] = useState(false)
  const [testUserId, setTestUserId] = useState()
  const [vendorData, setVendorData] = useState()
  const [appType, setAppType] = useState()
  const [requestId, setRequestId] = useState()

  const formData = useRef(null)
  const tokenKey = process.env.APP
  const apiUrl = process.env.API_URL

  const getToken = () => {
    if (!sessionStorage.getItem(tokenKey)) {
      throw new Error(`Could not find token: ${tokenKey}`)
    } else {
      return sessionStorage.getItem(tokenKey)
    }
  }

  const setToken = (token) => sessionStorage.setItem(tokenKey, token)
  const isAuthenticated = () => {
    try {
      return getToken()
    } catch (error) {
      return false
    }
  }
  const { initRaygun, sendError, sendSessionStorageError } = useRaygun()

  useEffect(() => {
    initRaygun(vendorSettings?.viewSettings?.trackerConfig)
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)

    const accessCode = searchParams.get('bmid') || searchParams.get('accessCode')

    if (accessCode) {
      try {
        if (/^[a-zA-Z0-9-]{6}$/.test(accessCode)) {
          sessionStorage.setItem('accessCode', accessCode)
          console.log('Access code set successfully.')
        } else {
          console.error('Invalid accessCode format.')
          sendError(new Error('Invalid accessCode format.'), 'access_code_error')
        }
      } catch (error) {
        sendError(error, 'access_code_error')
        if (error.name === 'QuotaExceededError') {
          console.error('Storage quota exceeded. Could not set access code.')
        } else {
          console.error('Failed to set accessCode in sessionStorage:', error)
        }
      }
    }
  }, [])

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 100)

    // eslint-disable-next-line
  });

  useEffect(() => {
    if (sessionStorage.getItem('accessCode')) {
      setAccessCode(sessionStorage.getItem('accessCode'))
    } else {
      setAccessCodeError(true)
    }
  }, [])

  useEffect(() => {
    if (accessCodeError) {
      sessionStorage.clear()
      localStorage.clear()
    }
  }, [accessCodeError])

  useEffect(() => {
    setRequestId(sessionStorage.getItem('request-id') || uuidv4())
  }, [])

  useEffect(() => {
    if (requestId) {
      if (!sessionStorage.getItem('request-id')) {
        sessionStorage.setItem('request-id', requestId)
      }
      axios.defaults.headers.common['request-id'] = requestId
    }
  }, [requestId])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const tusid = searchParams.get('tusid')

    setTestUserId(tusid)
    if (accessCode) {
      if (!accessCodeVerified) {
        verifyAccessCode(accessCode)
      }
    }
  }, [accessCode])

  const performApiRequest = (apiRequest, functionName) => {
    try {
      const token = getToken()
      apiRequest(token)
    } catch (error) {
      sendSessionStorageError(error, functionName)
    }
  }

  const verifyAccessCode = (code) => {
    get(null, `${apiUrl}/${code}`, null)
      .then((data) => {
        setAccessCodeVerified(true)
        setVendorData(data.data)
        setAppType(data.data.appType)
        if (data.data.config) {
          const config = data.data.config
          vendorSettings.customer = config.name
          vendorSettings.viewSettings.cancelMessage = config.cancelMessage

          const headerImage = document.getElementById('header-image')
          const footerImage = document.getElementById('footer-image')
          if (config.headerLogo) {
            headerImage.src = config.headerLogo
            headerImage.style.display = 'block'
          }
          if (config.footerLogo) {
            footerImage.src = config.footerLogo
            footerImage.style.display = 'block'
          }

          document.getElementById('logo-icon').href = config.favIcon || config.headerLogo || config.footerLogo
        }
      })
      .catch(() => {
        setAccessCodeError(true)
      })
  }

  const getAccessFrequencyInDays = ({ onSuccess, onError }) => {
    const url = `${apiUrl}/setting/${sessionStorage.getItem('accessCode')}/accessFrequencyInDays`

    get(null, url, null)
      .then((response) => {
        onSuccess(response.data)
      })
      .catch((error) => {
        onError(error)
      })
  }

  const handleSuccess = (data, successCallback) => {
    if (typeof successCallback === 'function') {
      successCallback(data)
    }
  }

  const logout = (callback) => {
    setCustomerLogin(null)
    sessionStorage.removeItem(tokenKey)
    if (typeof callback === 'function') {
      callback()
    }
  }

  const refreshToken = () => {
    if (refreshTokenTask == null) {
      const data = {
        token: sessionStorage.getItem(process.env.APP)
      }

      refreshTokenTask = new Promise((resolve) => {
        performApiRequest(() => {
          post(null, `${apiUrl}/${sessionStorage.getItem('accessCode')}/refresh`, null, data)
            .then((resp) => {
              sessionStorage.setItem(process.env.APP, resp.data.token)
              refreshTokenTask = null
              resolve()
            })
            .catch((error) => {
              sendError(error, 'refresh-token-error', 'refreshToken')
              sessionStorage.removeItem(process.env.APP)
              location.reload()
            })
        }, 'refreshToken')
      })
    }

    return refreshTokenTask
  }

  const login = ({ body, onSuccess, onError }) => {
    const url = testUserId ? `${apiUrl}/${accessCode}/code?testUserId=${testUserId}` : `${apiUrl}/${accessCode}/code`

    return post(null, url, null, body)
      .then(() => {
        setCustomerLogin(body)
        handleSuccess(null, onSuccess)
      })
      .catch((error) => onError(error))
  }

  const verifyAbn = ({ abn, name, position, onSuccess, onError }) => {
    const url = `${apiUrl}/${accessCode}/abn/${abn}`

    return get(null, url, null)
      .then((response) => {
        if (!response.data) {
          //For MVP
          const error = new Error('No valid name found')
          error.status = 404
          error.data = 'Invalid ABN'
          throw error
        }
        const givenName = getGivenName(response.data.legalGivenNames)
        const displayName =
          response.data.mainNonIndividualNameText ||
          (givenName ? givenName + ' ' + response.data.legalFamilyName : response.data.legalFamilyName)
        formData.current = { abn: abn, name: name, position: position, authorityAccepted: 'true' }
        handleSuccess(displayName, onSuccess)
      })
      .catch((error) => onError(error))
  }

  const getGivenName = (givenNames) => {
    return givenNames ? `${givenNames.replaceAll(',', ' ')}` : ''
  }

  const verifyCode = ({ code, customerLogin, onSuccess, onError }) => {
    const url = testUserId
      ? `${apiUrl}/${accessCode}/verify/${code}?testUserId=${testUserId}`
      : `${apiUrl}/${accessCode}/verify/${code}`

    return post(null, url, null, customerLogin)
      .then((response) => {
        handleSuccess(response.data, onSuccess)
      })
      .catch((error) => onError(error))
  }

  const onExit = () => {
    let url = window.location.origin + '?accessCode=' + sessionStorage.getItem('accessCode')
    if (sessionStorage.getItem('intro')) {
      url += `&intro=${sessionStorage.getItem('intro')}`
    }

    if (sessionStorage.getItem('whiteLabel')) {
      url += `&whiteLabel=${sessionStorage.getItem('whiteLabel')}`
    }
    sessionStorage.clear()
    localStorage.clear()

    window.location.href = url
  }

  const expire = () => {
    performApiRequest((token) => {
      return post(null, `${apiUrl}/expire`, token, null)
        .then(() => {
          sessionStorage.clear()
          localStorage.clear()
        })
        .catch((error) => {
          if (error.status === 401) {
            refreshToken().then(() => {
              expire(sessionStorage.getItem(process.env.APP))
            })
          } else {
            sendError(error, 'xhr-error', 'expire')
            sessionStorage.clear()
            localStorage.clear()
          }
        })
    }, 'expire')
  }

  const onSuccess = ({ successFn, onError, isAngle = true }) => {
    performApiRequest((token) => {
      let url = `${apiUrl}/success/${accessCode}/`
      if (!isAngle) {
        url += 'verifier/'
      }

      url += sessionStorage.getItem('transactionId')

      return post(null, url, token, null)
        .then(() => {
          successFn()
        })
        .catch((error) => {
          sendError(error, 'xhr-error', 'onSuccess')

          if (error.status === 401) {
            refreshToken().then(() => {
              const token = getToken()
              onSuccess({ token, successFn, onError })
            })
          } else {
            onError(error)
          }
        })
    }, 'onSuccess')
  }

  const getAccurassiResult = ({ onSuccess, onError }) => {
    performApiRequest((token) => {
      return get(null, `${apiUrl}/accurassi/result/${accessCode}`, token)
        .then((response) => {
          onSuccess(response.data)
        })
        .catch((error) => {
          sendError(error, 'xhr-error', 'getAccurassiResult')

          if (error.status === 401) {
            refreshToken().then(() => {
              const token = getToken()
              getAccurassiResult({ token, onSuccess, onError })
            })
          } else {
            onError(error)
          }
        })
    }, 'getAccurassiResult')
  }

  const getSolarResult = ({ onSuccess, onError }) => {
    performApiRequest((token) => {
      return get(null, `${apiUrl}/solar/result/${accessCode}`, token)
        .then((response) => {
          onSuccess(response.data)
        })
        .catch((error) => {
          sendError(error, 'xhr-error', 'getSolarResult')
          if (error.status === 401) {
            refreshToken().then(() => {
              const token = getToken()
              getSolarResult({ token, onSuccess, onError })
            })
          } else {
            onError(error)
          }
        })
    }, 'getSolarResult')
  }

  const getLoginUrl = () => {
    return testUserId ? `${apiUrl}/${accessCode}/code?testUserId=${testUserId}` : `${apiUrl}/${accessCode}/code`
  }

  return (
    <ApplicationContext.Provider
      value={{
        customerLogin,
        setCustomerLogin,
        isAuthenticated,
        login,
        logout,
        verifyCode,
        expire,
        refreshToken,
        getToken,
        setToken,
        onExit,
        appType,
        vendorSettings,
        accessCodeError,
        accessCodeVerified,
        vendorData,
        verifyAbn,
        formData,
        getAccessFrequencyInDays,
        getAccurassiResult,
        onSuccess,
        getSolarResult,
        requestId,
        apiUrl,
        testUserId,
        accessCode,
        getLoginUrl
      }}
    >
      {children}
    </ApplicationContext.Provider>
  )
}
